import type Phaser from "phaser";

type AutomationTargetType = "button" | "label";

type RectangleLike = {
  x: number;
  y: number;
  width: number;
  height: number;
  centerX?: number;
  centerY?: number;
};

type AutomationEntry = {
  type: AutomationTargetType;
  name: string;
  scene: Phaser.Scene;
  getBounds: () => RectangleLike;
  getText?: () => string;
};

export type AutomationSnapshot = {
  name: string;
  type: AutomationTargetType;
  scene: string;
  center: { x: number; y: number };
  text?: string;
};

export type AutomationStore = {
  targets: Set<AutomationEntry>;
  listTargets: () => AutomationSnapshot[];
  getTarget: (name: string) => AutomationSnapshot | undefined;
  overrides: Record<string, unknown>;
};

type AutomationWindow = Window & {
  __automation?: AutomationStore | { overrides: Record<string, unknown> };
};

const getAutomationWindow = (): AutomationWindow | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return window as AutomationWindow;
};

const snapshotEntry = (entry: AutomationEntry): AutomationSnapshot | undefined => {
  const camera = entry.scene.cameras.main;
  const view = camera.worldView;
  const canvasRect = entry.scene.game.canvas.getBoundingClientRect();

  if (view.width === 0 || view.height === 0 || canvasRect.width === 0 || canvasRect.height === 0) {
    return undefined;
  }

  const bounds = entry.getBounds();
  const centerX = bounds.centerX ?? bounds.x + bounds.width / 2;
  const centerY = bounds.centerY ?? bounds.y + bounds.height / 2;

  const normalizedX = (centerX - view.x) / view.width;
  const normalizedY = (centerY - view.y) / view.height;

  return {
    name: entry.name,
    type: entry.type,
    scene: entry.scene.scene.key,
    center: {
      x: canvasRect.left + normalizedX * canvasRect.width,
      y: canvasRect.top + normalizedY * canvasRect.height,
    },
    text: entry.getText?.(),
  };
};

const createStore = (initialOverrides: Record<string, unknown> = {}): AutomationStore => {
  const targets = new Set<AutomationEntry>();

  const listTargets = (): AutomationSnapshot[] =>
    Array.from(targets)
      .map((target) => snapshotEntry(target))
      .filter((target): target is AutomationSnapshot => Boolean(target));

  const getTarget = (name: string): AutomationSnapshot | undefined =>
    listTargets().find((target) => target.name === name);

  return { targets, listTargets, getTarget, overrides: initialOverrides };
};

const ensureStore = (): AutomationStore | null => {
  const automationWindow = getAutomationWindow();
  if (!automationWindow) {
    return null;
  }

  const current = automationWindow.__automation;
  if (!current || !("targets" in current)) {
    const initialOverrides = (current as { overrides?: Record<string, unknown> })?.overrides || {};
    automationWindow.__automation = createStore(initialOverrides);
  }

  return automationWindow.__automation as AutomationStore;
};

export const registerAutomationTarget = (
  scene: Phaser.Scene,
  target: {
    type: AutomationTargetType;
    name: string;
    getBounds: () => RectangleLike;
    getText?: () => string;
  }
): (() => void) => {
  const store = ensureStore();
  if (!store) {
    return () => undefined;
  }

  const entry: AutomationEntry = {
    type: target.type,
    name: target.name,
    scene,
    getBounds: target.getBounds,
    getText: target.getText,
  };

  store.targets.add(entry);

  return () => {
    store.targets.delete(entry);
  };
};

export const tagTextForAutomation = (text: Phaser.GameObjects.Text, name: string): (() => void) => {
  const cleanup = registerAutomationTarget(text.scene, {
    type: "label",
    name,
    getBounds: () => text.getBounds(),
    getText: () => text.text,
  });

  text.once("destroy", cleanup);

  return cleanup;
};

export const listAutomationTargets = (): AutomationSnapshot[] => ensureStore()?.listTargets() ?? [];

export const getAutomationTarget = (name: string): AutomationSnapshot | undefined =>
  ensureStore()?.getTarget(name);

export const getAutomationOverride = <T>(key: string): T | undefined =>
  ensureStore()?.overrides[key] as T | undefined;

declare global {
  interface Window {
    __automation?: AutomationStore | { overrides: Record<string, unknown> };
  }
}
